import { Heading } from "components/Theme/heading";
import { Form, Formik } from "formik";
import type {
  BasicAuthCredentials,
  GatewayAssetOut,
  MQTTCredentials,
  PSKCredentials,
  PublicKeyCredentials,
  SimConnectivityState,
} from "kubb";
import { appConfig } from "../../../../Config";
import { ErrorBanner } from "../../../../components/Error/ErrorBanner";
import { ButtonGroupField } from "../../../../components/Forms/ButtonGroup";
import NewTextFormComponent from "../../../../components/Forms/NewTextFormComponent";
import BlockSpinner from "../../../../components/Spinners/BlockSpinner";
import { Button } from "../../../../components/Theme/button";
import CopyToClipboard from "../../../../components/utils/CopyToClipboard";
import { useUpdateAsset } from "../../../../hooks/updateEntity";
import SimConnectivityStateLabel from "../sim_cards/SimConnectivityStateLabel";

enum AuthTypes {
  basic_auth = "basic_auth",
  public_key = "public_key",
}

interface connectivitySettings {
  postUrl: string;
  sftpServer: string;
  asset_user: string;
  password?: string;
  authenticationType: AuthTypes;
  publicKey?: string;
}

function isCredentialsBasic(
  credentials: BasicAuthCredentials | PublicKeyCredentials | MQTTCredentials,
): credentials is BasicAuthCredentials {
  return (credentials as BasicAuthCredentials).type === AuthTypes.basic_auth;
}

function isCredentialsPublicKey(
  credentials: BasicAuthCredentials | PublicKeyCredentials | MQTTCredentials,
): credentials is PublicKeyCredentials {
  return (credentials as PublicKeyCredentials).type === AuthTypes.public_key;
}

function isCredentialsMQTT(
  credentials: BasicAuthCredentials | PublicKeyCredentials | MQTTCredentials,
): credentials is MQTTCredentials {
  return (credentials as MQTTCredentials).type === "mqtt";
}

function isCredentialsEdgePSK(
  credentials: GatewayAssetOut["credentials"],
): credentials is PSKCredentials {
  // PSKCredentials has type="psk" with nested bootstrap/dm structure
  const creds = credentials as PSKCredentials;
  return creds.type === "psk" && ("bootstrap" in creds || "dm" in creds);
}

function GatewayConnectionDisplay(props: { gateway: GatewayAssetOut }) {
  const { gateway } = props;
  const { update, loading, error } = useUpdateAsset(gateway);

  // Handle Edge PSK credentials separately
  if (isCredentialsEdgePSK(gateway.credentials)) {
    return (
      <>
        <PSKCredentialsSection credentials={gateway.credentials} />
        {gateway.sim_card_iccid && <SimConnectivitySection gateway={gateway} />}
      </>
    );
  }

  // At this point, we know credentials is not EdgePSK (handled by early return above)
  const credentials = gateway.credentials as
    | BasicAuthCredentials
    | PublicKeyCredentials
    | MQTTCredentials;

  const isCMe2100 = gateway.model && gateway.model.indexOf("21") !== -1;

  const initialValues: connectivitySettings = {
    postUrl: isCMe2100
      ? `${appConfig().hes.cme2100}${gateway.company_id}`
      : `${appConfig().hes.cme}/${gateway.company_id}`,
    sftpServer: import.meta.env.VITE_SFTP_SERVER,
    asset_user: credentials.username || "",
    password:
      isCredentialsBasic(credentials) || isCredentialsMQTT(credentials)
        ? credentials.password
        : "",
    publicKey: isCredentialsPublicKey(credentials)
      ? credentials.public_key
      : "",
    authenticationType: credentials.type as AuthTypes,
  };

  const canSwitch: boolean = gateway.manufacturer === "evinox";

  function updatedGateway(
    formValues: connectivitySettings,
  ): BasicAuthCredentials | PublicKeyCredentials {
    switch (formValues.authenticationType) {
      case AuthTypes.basic_auth:
        return {
          username: credentials.username,
          type: formValues.authenticationType,
          password: formValues.password,
        } as BasicAuthCredentials;

      case AuthTypes.public_key:
        return {
          username: credentials.username,
          type: formValues.authenticationType,
          public_key: formValues.publicKey,
        } as PublicKeyCredentials;
      default:
        throw new Error("Unsupported authentication type");
    }
  }

  const submit = (formValues: connectivitySettings) => {
    update({
      ...gateway,
      credentials: updatedGateway(formValues),
    });
  };
  return (
    <>
      <Heading>Gateway Connectivity Settings</Heading>

      <Formik initialValues={initialValues} onSubmit={submit}>
        {({ values }) => {
          const basicAuth: boolean = values.authenticationType === "basic_auth";

          return (
            <Form>
              <BlockSpinner loading={loading}>
                {canSwitch && (
                  <ButtonGroupField
                    fieldName={"authenticationType"}
                    label={{ label: "Credentials type" }}
                    options={[
                      {
                        value: "basic_auth",
                        label: "Basic",
                      },
                      {
                        value: "public_key",
                        label: "Key pair",
                      },
                    ]}
                  />
                )}

                {gateway.manufacturer === "elvaco" ? (
                  <NewTextFormComponent
                    fieldName={"postUrl"}
                    label={{ label: "Post URL" }}
                    staticValue
                    disabled
                    clipboard
                  />
                ) : (
                  <NewTextFormComponent
                    fieldName={"sftpServer"}
                    label={{ label: "SFTP Server" }}
                    staticValue
                    disabled
                    clipboard
                  />
                )}
                <NewTextFormComponent
                  fieldName={"asset_user"}
                  label={{ label: "Username" }}
                  staticValue
                  disabled
                  clipboard
                />
                {basicAuth ? (
                  <NewTextFormComponent
                    fieldName={"password"}
                    label={{ label: "Password" }}
                    staticValue
                    disabled
                    clipboard
                  />
                ) : (
                  <NewTextFormComponent
                    fieldName={"publicKey"}
                    label={{ label: "SSH Public Key" }}
                  />
                )}
                {gateway.manufacturer === "evinox" && (
                  <>
                    <span className={"float-right"}>
                      <Button
                        type={"submit"}
                        color={"brandLight"}
                        disabled={loading}
                      >
                        Update SSH Settings
                      </Button>
                    </span>
                    <ErrorBanner error={error} />
                  </>
                )}
              </BlockSpinner>
            </Form>
          );
        }}
      </Formik>

      {gateway.sim_card_iccid && <SimConnectivitySection gateway={gateway} />}
    </>
  );
}

function PSKCredentialsSection(props: {
  credentials: PSKCredentials;
}) {
  const { credentials } = props;

  return (
    <>
      <Heading>LWM2M Connectivity Settings</Heading>

      {credentials.bootstrap && (
        <>
          <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">
            Bootstrap Server
          </h4>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-gray-500">Identity</dt>
            <dd>
              <CopyToClipboard
                value={credentials.bootstrap.identity}
                className="cursor-pointer hover:text-blue-600"
              />
            </dd>
            <dt className="text-gray-500">PSK</dt>
            <dd>
              <CopyToClipboard
                value={credentials.bootstrap.psk_key}
                className="cursor-pointer hover:text-blue-600 font-mono text-xs"
                privacyMode
              />
            </dd>
          </dl>
        </>
      )}

      {credentials.dm && (
        <>
          <h4 className="text-sm font-medium text-gray-700 mt-4 mb-2">
            Device Management Server
          </h4>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
            <dt className="text-gray-500">Identity</dt>
            <dd>
              <CopyToClipboard
                value={credentials.dm.identity}
                className="cursor-pointer hover:text-blue-600"
              />
            </dd>
            <dt className="text-gray-500">PSK</dt>
            <dd>
              <CopyToClipboard
                value={credentials.dm.psk_key}
                className="cursor-pointer hover:text-blue-600 font-mono text-xs"
                privacyMode
              />
            </dd>
          </dl>
        </>
      )}

      {!credentials.bootstrap && !credentials.dm && (
        <p className="text-sm text-gray-500 italic">
          No PSK credentials configured
        </p>
      )}
    </>
  );
}

function SimConnectivitySection(props: { gateway: GatewayAssetOut }) {
  const simState = props.gateway
    .sim_connectivity_state as SimConnectivityState | null;

  return (
    <>
      <Heading>
        SIM Connectivity{" "}
        <SimConnectivityStateLabel state={simState} size={"sm"} />
      </Heading>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-gray-500">ICCID</dt>
        <dd>{props.gateway.sim_card_iccid}</dd>

        <dt className="text-gray-500">Status</dt>
        <dd>{simState?.connection_status ?? "Unknown"}</dd>

        <dt className="text-gray-500">Technology</dt>
        <dd>
          {simState?.pdp_context?.rat_type_description ??
            simState?.pdp_context?.rat_type ??
            "-"}
        </dd>

        <dt className="text-gray-500">IP Address</dt>
        <dd>{simState?.pdp_context?.ue_ip_address ?? "-"}</dd>

        <dt className="text-gray-500">Operator</dt>
        <dd>{simState?.network_operator?.operator_name ?? "-"}</dd>

        <dt className="text-gray-500">Country</dt>
        <dd>{simState?.network_operator?.country ?? "-"}</dd>

        <dt className="text-gray-500">MCC/MNC</dt>
        <dd>
          {simState?.network_operator?.mcc && simState?.network_operator?.mnc
            ? `${simState.network_operator.mcc}/${simState.network_operator.mnc}`
            : "-"}
        </dd>
      </dl>
    </>
  );
}

export default GatewayConnectionDisplay;
